import { html, nothing } from "lit";
import { renderFleetPlanAgentCards } from "../fleet/fleet-plan-agent-cards.ts";
import { renderFleetPlanMiniDag } from "../fleet/fleet-plan-dag.ts";
import {
  fleetAutonomyLabel,
  fleetAutonomyScopeLabel,
  fleetPlanAgentCount,
  fleetPlanStatusLabel,
  fleetPlanUnitCount,
  fleetStatusPillClass,
  fleetUnitSpawnAllowed,
  fleetUnitStatusLabel,
  fleetUnitStatusTitle,
  planHasRunningUnits,
  planNeedsOperatorDecision,
} from "../fleet/fleet-plan-labels.ts";
import { renderFleetPlanPhaseStepper } from "../fleet/fleet-plan-phase-stepper.ts";
import { renderBibleFinalSummaryCard } from "../fleet/fleet-plan-summary-view.ts";
import { formatAgo } from "../format.ts";
import type { HelpTopicId } from "../help/help-content.ts";
import { icons } from "../icons.ts";
import type {
  AgentsListResult,
  BibleFinalSummary,
  FleetDecisionItem,
  FleetWorkPlanRecord,
  FleetWorkPlanSummary,
  FleetWorkUnit,
} from "../types.ts";

export type AgentsWorkPlanProps = {
  agentId: string;
  agentsList: AgentsListResult | null;
  loading: boolean;
  error: string | null;
  plans: FleetWorkPlanSummary[];
  youtrackConfigured: boolean;
  selectedPlanId: string | null;
  planDetail: FleetWorkPlanRecord | null;
  detailLoading: boolean;
  busy: boolean;
  createGoal: string;
  createProject: string;
  createParentSessionKey: string;
  createTargetBranch: string;
  branches: string[];
  branchesLoading: boolean;
  onCreateGoalChange: (value: string) => void;
  onCreateProjectChange: (value: string) => void;
  onCreateParentSessionKeyChange: (value: string) => void;
  onCreateTargetBranchChange: (value: string) => void;
  onRefresh: () => void;
  onCreatePlan: () => void;
  onSelectPlan: (planId: string) => void;
  onApprove: (planId: string) => void;
  onPublishYouTrack: (planId: string, project: string) => void;
  onSpawnUnit: (planId: string, unitId: string, workerAgentId?: string) => void;
  onStopPlan: (planId: string) => void;
  onResumePlan: (planId: string) => void;
  onUndoPlan: (planId: string) => void;
  openDecisions: FleetDecisionItem[];
  bibleSummary: BibleFinalSummary | null;
  onResolveDecision: (planId: string, decisionId: string, resolution: "continue" | "stop") => void;
  needsDecisionOnly: boolean;
  onNeedsDecisionOnlyChange: (value: boolean) => void;
  onOpenHelp: (topic: HelpTopicId) => void;
  expandedRoleIds: Set<string>;
  onToggleExpandedRole: (roleId: string) => void;
  nowMs?: number;
  sessionLogs?: Record<string, import("../fleet/fleet-plan-session-logs.ts").FleetSessionLogLine[]>;
};

function isTerminalPlan(status: string): boolean {
  return status === "completed" || status === "failed" || status === "abandoned";
}

function decisionContinueLabel(kind: FleetDecisionItem["kind"]): string {
  return kind === "blocked" ? "Unblock and re-queue" : "Clear decision gate";
}

function decisionConsequence(kind: FleetDecisionItem["kind"]): string {
  if (kind === "blocked") {
    return "Unblocking re-queues this unit as Planned. Stop preserves work and writes a checkpoint.";
  }
  return "Clearing the gate lets dispatch resume. Stop preserves work and writes a checkpoint.";
}

function spawnTitle(unit: FleetWorkUnit): string {
  if (unit.status === "blocked") {
    return "Resolve the decision before spawning this unit";
  }
  if (unit.status === "running") {
    return "Unit is already running";
  }
  if (!fleetUnitSpawnAllowed(unit.status)) {
    return `Cannot spawn a ${fleetUnitStatusLabel(unit.status).toLowerCase()} unit`;
  }
  return "Spawn a subagent for this unit";
}

export function renderAgentsWorkPlan(props: AgentsWorkPlanProps) {
  const agents = props.agentsList?.agents ?? [];
  const plannerId = props.agentId;
  const selected = props.planDetail;
  const publishProject =
    props.createProject.trim() || selected?.youtrackProject?.trim() || "JAISIU";
  const decisionPlanCount = props.plans.filter((p) => planNeedsOperatorDecision(p)).length;
  const visiblePlans = props.needsDecisionOnly
    ? props.plans.filter((p) => planNeedsOperatorDecision(p))
    : props.plans;
  const selectedAgentCount = selected ? fleetPlanAgentCount(selected) : 0;
  const selectedUnitCount = selected ? fleetPlanUnitCount(selected) : 0;
  const stopDisabled =
    props.busy ||
    !selected ||
    selected.status === "stopped" ||
    selected.status === "stopping" ||
    isTerminalPlan(selected.status);

  return html`
    <section class="card work-plan">
      <header class="work-plan__header">
        <div>
          <h2 class="work-plan__title">Work plan</h2>
          <p class="work-plan__lede">
            Decompose a goal into units, dispatch subagents, and resolve blockers. Planner:
            <span class="mono">${plannerId}</span>
          </p>
        </div>
        <div class="work-plan__header-actions">
          ${
            decisionPlanCount > 0
              ? html`<span class="work-plan__badge" data-testid="work-plan-decisions-badge"
                  >${decisionPlanCount} need${decisionPlanCount === 1 ? "s" : ""} decision</span
                >`
              : nothing
          }
          <button
            class="work-plan__help"
            type="button"
            title="Help: work plans"
            @click=${() => props.onOpenHelp("fleet-work-plan")}
          >
            ${icons.helpCircle}
          </button>
          <button
            class="btn btn--sm"
            type="button"
            ?disabled=${props.loading || props.busy}
            @click=${props.onRefresh}
          >
            ${props.loading ? "Loading…" : "Refresh"}
          </button>
        </div>
      </header>

      ${props.error ? html`<div class="callout danger">${props.error}</div>` : nothing}

      <div class="work-plan__layout">
        <div class="work-plan__composer">
          <div class="field-label">New plan goal</div>
          <textarea
            class="input"
            rows="4"
            placeholder="Describe the epic. Use steps: First …, then …, finally …"
            .value=${props.createGoal}
            @input=${(e: Event) =>
              props.onCreateGoalChange((e.target as HTMLTextAreaElement).value)}
          ></textarea>
          <div class="field-row">
            <label class="field-label">YouTrack project</label>
            <input
              class="input"
              type="text"
              .value=${props.createProject}
              @input=${(e: Event) =>
                props.onCreateProjectChange((e.target as HTMLInputElement).value)}
            />
          </div>
          <div class="field-row">
            <label class="field-label">Target branch</label>
            <input
              class="input mono"
              type="text"
              placeholder="develop"
              list="work-plan-target-branch-list"
              .value=${props.createTargetBranch}
              @input=${(e: Event) =>
                props.onCreateTargetBranchChange((e.target as HTMLInputElement).value)}
            />
            <datalist id="work-plan-target-branch-list">
              ${props.branches.map((branch) => html`<option value=${branch}></option>`)}
            </datalist>
            ${
              props.branchesLoading
                ? html`<span class="muted" data-testid="work-plan-branches-loading"
                    >Loading branches…</span
                  >`
                : nothing
            }
          </div>
          <div class="field-row">
            <label class="field-label">Parent session key (optional)</label>
            <input
              class="input mono"
              type="text"
              placeholder="agent:main:main"
              .value=${props.createParentSessionKey}
              @input=${(e: Event) =>
                props.onCreateParentSessionKeyChange((e.target as HTMLInputElement).value)}
            />
          </div>
          <button
            class="btn btn--sm primary"
            type="button"
            ?disabled=${props.busy || !props.createGoal.trim()}
            @click=${props.onCreatePlan}
          >
            ${props.busy ? "Working…" : "Create plan"}
          </button>
          <span class="muted">
            YouTrack: ${props.youtrackConfigured ? "configured" : "not configured (env token)"}
          </span>

          <div class="work-plan__list-head">
            <div class="field-label">Saved plans</div>
            <label class="work-plan__filter">
              <input
                type="checkbox"
                .checked=${props.needsDecisionOnly}
                @change=${(e: Event) =>
                  props.onNeedsDecisionOnlyChange((e.target as HTMLInputElement).checked)}
              />
              Needs decision
            </label>
          </div>
          ${
            visiblePlans.length === 0
              ? html`
                  <div class="work-plan__empty">
                    ${
                      props.needsDecisionOnly
                        ? "No plans are waiting on a decision."
                        : "No plans yet for this agent workspace."
                    }
                  </div>
                `
              : visiblePlans.map(
                  (plan) => html`
                    <button
                      type="button"
                      class="work-plan__plan ${props.selectedPlanId === plan.id ? "is-active" : ""}"
                      @click=${() => props.onSelectPlan(plan.id)}
                    >
                      <div class="work-plan__plan-title">${plan.goal.slice(0, 80)}</div>
                      <div class="work-plan__plan-meta">
                        <span class=${fleetStatusPillClass(plan.status)}
                          >${fleetPlanStatusLabel(plan.status)}</span
                        >
                        ${
                          planNeedsOperatorDecision(plan)
                            ? html`<span class="pill pill--sm pill--blocked">Needs decision</span>`
                            : nothing
                        }
                        ${
                          planHasRunningUnits(plan)
                            ? html`<span class="pill pill--sm pill--running">In progress</span>`
                            : nothing
                        }
                        <span
                          >${plan.doneCount}/${plan.unitCount} done ·
                          ${formatAgo(plan.updatedAtMs)}</span
                        >
                      </div>
                    </button>
                  `,
                )
          }
        </div>

        <div class="work-plan__detail">
          ${
            props.detailLoading
              ? html`<div class="muted">Loading plan…</div>`
              : !selected
                ? html`
                    <div class="work-plan__empty">
                      Select a plan or create one to see the state machine.
                    </div>
                  `
                : html`
                    ${
                      props.openDecisions.length > 0
                        ? html`
                            <div class="work-plan-decisions" data-testid="work-plan-decisions">
                              <div class="field-label">Decisions</div>
                              ${props.openDecisions.map(
                                (d) => html`
                                  <div class="work-plan-decisions__item">
                                    <div>
                                      <strong>${d.title}</strong>
                                      <span
                                        class=${d.kind === "blocked" ? fleetStatusPillClass("blocked") : "pill pill--sm pill--warn"}
                                        >${d.kind === "blocked" ? "Blocked unit" : "Repair limit"}</span
                                      >
                                    </div>
                                    <div class="muted">${d.reason}</div>
                                    <div class="work-plan-decisions__consequence">
                                      ${decisionConsequence(d.kind)}
                                    </div>
                                    <div class="work-plan__toolbar-group">
                                      <button
                                        class="btn btn--sm primary"
                                        type="button"
                                        ?disabled=${props.busy}
                                        @click=${() =>
                                          props.onResolveDecision(d.planId, d.id, "continue")}
                                      >
                                        ${decisionContinueLabel(d.kind)}
                                      </button>
                                      <button
                                        class="btn btn--sm"
                                        type="button"
                                        title="Halt live units and preserve work"
                                        ?disabled=${props.busy}
                                        @click=${() =>
                                          props.onResolveDecision(d.planId, d.id, "stop")}
                                      >
                                        Stop plan
                                      </button>
                                    </div>
                                  </div>
                                `,
                              )}
                            </div>
                          `
                        : nothing
                    }
                    <div class="work-plan__exec-bar" data-testid="work-plan-exec-bar">
                      <span class="work-plan__meta-chip" data-testid="work-plan-agent-count">
                        ${icons.users}
                        ${`${selectedAgentCount} agent${selectedAgentCount === 1 ? "" : "s"}`}
                      </span>
                      <span class="work-plan__meta-chip" data-testid="work-plan-unit-count">
                        ${icons.list}
                        ${`${selectedUnitCount} task${selectedUnitCount === 1 ? "" : "s"}`}
                      </span>
                      ${
                        selected.targetBranch
                          ? html`<span
                              class="work-plan__meta-chip work-plan__meta-chip--branch mono"
                              title="Target branch"
                              >${selected.targetBranch}</span
                            >`
                          : nothing
                      }
                    </div>
                    ${renderFleetPlanPhaseStepper({ phase: selected.planPhase })}

                    <div class="work-plan__toolbar">
                      <span class=${fleetStatusPillClass(selected.status)} title=${selected.status}
                        >${fleetPlanStatusLabel(selected.status)}</span
                      >
                      <span
                        class="pill pill--sm ${selected.autonomyLevel === "manual" ? "pill--blocked" : selected.autonomyLevel === "autonomous" ? "pill--running" : "pill--warn"}"
                        title="Bible §14 — Manual blocks mutating shell and workspace writes. Confirmation asks before shell/merge/push; workspace file writes still run. Autonomous runs without asking."
                        >${fleetAutonomyLabel(selected.autonomyLevel)}</span
                      >
                      <span
                        class="muted"
                        title="Bible §14 / O-8 — tools this order may use (set at planning time)"
                        >${fleetAutonomyScopeLabel(selected.autonomyScope)}</span
                      >
                      ${
                        selected.parentYoutrackIssueReadable
                          ? html`<span class="mono muted"
                              >${selected.parentYoutrackIssueReadable}</span
                            >`
                          : nothing
                      }
                    </div>

                    <div class="work-plan__goal">${selected.goal}</div>
                    ${
                      selected.status === "stopped" && selected.lastCheckpointAtMs
                        ? html`
                            <div class="work-plan-checkpoint">
                              Plan stopped. Checkpoint saved
                              ${formatAgo(selected.lastCheckpointAtMs)} — resume re-queues stopped
                              units as Planned. Restore checkpoint does not revert git or YouTrack.
                            </div>
                          `
                        : nothing
                    }

                    <div class="work-plan__toolbar">
                      <div class="work-plan__toolbar-group">
                        ${
                          selected.status === "draft"
                            ? html`
                                <button
                                  class="btn btn--sm"
                                  type="button"
                                  ?disabled=${props.busy}
                                  @click=${() => props.onApprove(selected.id)}
                                >
                                  Approve
                                </button>
                              `
                            : nothing
                        }
                        <button
                          class="btn btn--sm"
                          type="button"
                          title="Stop live units and preserve work"
                          ?disabled=${stopDisabled}
                          @click=${() => props.onStopPlan(selected.id)}
                        >
                          Stop
                        </button>
                        <button
                          class="btn btn--sm"
                          type="button"
                          title="Resume a stopped plan"
                          ?disabled=${props.busy || selected.status !== "stopped"}
                          @click=${() => props.onResumePlan(selected.id)}
                        >
                          Resume
                        </button>
                      </div>
                      <div class="work-plan__toolbar-group">
                        <button
                          class="btn btn--sm"
                          type="button"
                          title="Restore registry from last stop checkpoint (does not revert git/API)"
                          ?disabled=${props.busy || !selected.lastCheckpointAtMs}
                          @click=${() => props.onUndoPlan(selected.id)}
                        >
                          Restore checkpoint
                        </button>
                        <button
                          class="btn btn--sm"
                          type="button"
                          ?disabled=${props.busy || !props.youtrackConfigured}
                          title=${
                            props.youtrackConfigured
                              ? "Create parent + subtasks in YouTrack"
                              : "Set YOUTRACK_URL and YOUTRACK_TOKEN on gateway"
                          }
                          @click=${() => props.onPublishYouTrack(selected.id, publishProject)}
                        >
                          Publish YouTrack
                        </button>
                      </div>
                    </div>

                    ${renderFleetPlanMiniDag(selected.units)}
                    ${renderFleetPlanAgentCards({
                      plan: selected,
                      expandedRoleIds: props.expandedRoleIds,
                      onToggleExpandedRole: props.onToggleExpandedRole,
                      nowMs: props.nowMs ?? Date.now(),
                      sessionLogs: props.sessionLogs,
                    })}
                    ${renderBibleFinalSummaryCard(props.bibleSummary, {
                      planStatus: selected.status,
                      requiresDecision: selected.requiresDecision,
                      plan: selected,
                    })}

                    <details class="work-plan-units-wrap">
                      <summary>Unit dispatch</summary>
                      <table class="table table--compact work-plan-units">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Unit</th>
                            <th>Context</th>
                            <th>Status</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          ${selected.units.map(
                            (unit) => html`
                              <tr>
                                <td class="mono">${unit.priority}</td>
                                <td>
                                  <div class="list-title">${unit.title}</div>
                                  <div class="list-sub muted">
                                    ${unit.description.slice(0, 120)}
                                  </div>
                                  ${
                                    unit.youtrackIssueReadable
                                      ? html`<div class="mono muted">
                                          ${unit.youtrackIssueReadable}
                                        </div>`
                                      : nothing
                                  }
                                </td>
                                <td>
                                  <span
                                    class=${unit.contextValid ? "pill pill--ok" : "pill pill--warn"}
                                    >${unit.contextValid ? "OK" : "Review"}</span
                                  >
                                  ${
                                    unit.estimatedInputTokens
                                      ? html`<div class="muted mono">
                                          ~${unit.estimatedInputTokens} tok
                                        </div>`
                                      : nothing
                                  }
                                </td>
                                <td title=${fleetUnitStatusTitle(unit.status)}>
                                  <span class=${fleetStatusPillClass(unit.status)}
                                    >${fleetUnitStatusLabel(unit.status)}</span
                                  >
                                  ${
                                    unit.status === "blocked"
                                      ? html`<div class="muted">Decision required</div>`
                                      : nothing
                                  }
                                  ${
                                    unit.blockedReason
                                      ? html`<div class="muted">${unit.blockedReason}</div>`
                                      : nothing
                                  }
                                </td>
                                <td>
                                  <button
                                    class="btn btn--sm"
                                    type="button"
                                    title=${spawnTitle(unit)}
                                    ?disabled=${props.busy || !fleetUnitSpawnAllowed(unit.status)}
                                    @click=${() =>
                                      props.onSpawnUnit(selected.id, unit.id, unit.targetAgentId)}
                                  >
                                    Spawn
                                  </button>
                                </td>
                              </tr>
                            `,
                          )}
                        </tbody>
                      </table>
                    </details>

                    <div class="muted">
                      Workers: ${agents.map((a) => a.id).join(", ") || plannerId}. Spawn uses
                      planner session unless parent session key is set on the plan.
                    </div>
                  `
          }
        </div>
      </div>
    </section>
  `;
}
